import {
  aRacheterEnClair,
  type GroupeCourses,
  lienBoutique,
  pourquoiEnClair,
  titreGamme,
} from "./bobines-textes";
import { CARTE, FOCUS, LongOuCourt, Pastille } from "./bobines-ui";
import type { VueBobines } from "./contract";
import { compter } from "./format";

/**
 * La liste de courses, en tete de la page (spec, section 4 ; maquettes `Bobines.dc.html` et
 * `BobinesMobile.dc.html`) : les references sous leur minimum, groupees par gamme, avec un lien
 * « Boutique Bambu » par gamme quand sa fiche est connue, puis le total. Chaque ligne dit ses
 * neuves et sa reserve face au minimum (« 1 neuve + 1 en réserve, minimum 3 ») ; une couleur
 * sans bobine dans l'AMS mais avec de la reserve se dit « en réserve seulement ». Le backend l'a
 * deja calculee et ordonnee (`regles.ts`) : la page ne retrie rien.
 */

/** La fleche d'un lien qui sort, en caractere de texte (U+FE0E : sans, Windows dessine un emoji). */
const FLECHE_SORTIE = String.fromCodePoint(0x2197, 0xfe0e);

export function CarteCourses({ vue }: { vue: VueBobines }) {
  const suivies = vue.gammes.reduce(
    (total, gamme) => total + gamme.references.filter((reference) => reference.minimum > 0).length,
    0,
  );
  const total = compter(vue.total_a_racheter, "bobine", "bobines");
  return (
    <section aria-labelledby="bobines-courses" className={CARTE}>
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <h2 className="text-[15px] font-semibold" id="bobines-courses">
          À racheter
        </h2>
        {vue.total_a_racheter > 0 ? (
          <span className="rounded-full bg-racheter/10 px-2.5 py-1 text-[13px] font-medium text-racheter">
            {total}
          </span>
        ) : null}
      </div>
      {vue.courses.length === 0 ? (
        <RienARacheter suivies={suivies} />
      ) : (
        <>
          <div>
            {vue.courses.map((groupe) => (
              <Groupe groupe={groupe} key={JSON.stringify([groupe.marque, groupe.gamme])} />
            ))}
          </div>
          <p className="mt-3 flex justify-between gap-3 border-t border-border pt-3 text-sm font-semibold">
            <span>Total</span>
            <LongOuCourt
              court={total}
              long={`${total}, ${compter(vue.courses.length, "gamme", "gammes")}`}
            />
          </p>
          <p className="mt-3 hidden text-[13px] text-muted-foreground md:block">
            Le minimum compte les bobines neuves, celles de l'AMS et celles en réserve : dès qu'une
            bobine est commencée, elle ne compte plus.
          </p>
        </>
      )}
    </section>
  );
}

/** Maquette `BobinesEtats.dc.html`, etat 5. */
function RienARacheter({ suivies }: { suivies: number }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm">Rien à racheter.</p>
      <p className="text-[13px] text-muted-foreground">
        {suivies === 0
          ? "Aucune couleur n'a encore de minimum : règle-le dans le stock, avec - et +, pour qu'elle monte ici quand il en manque."
          : `${suivies === 1 ? "1 couleur suivie, à son minimum" : `${suivies} couleurs suivies, toutes à leur minimum`}. Les autres ont un minimum de 0 : elles ne se rachètent jamais toutes seules.`}
      </p>
    </div>
  );
}

function Groupe({ groupe }: { groupe: GroupeCourses }) {
  const titre = titreGamme(groupe);
  const lien = lienBoutique(groupe.marque, groupe.gamme);
  return (
    <div className="border-t border-border/60 pt-3.5 first:border-t-0 first:pt-1">
      <div className="flex items-baseline justify-between gap-2.5 pb-1">
        <h3 className="text-sm font-semibold">{titre}</h3>
        {lien === null ? null : (
          <a
            aria-label={`Boutique Bambu : ${titre} (nouvel onglet)`}
            className={`rounded-sm text-[13.5px] font-medium underline underline-offset-[3px] ${FOCUS}`}
            href={lien}
            rel="noopener noreferrer"
            target="_blank"
          >
            <LongOuCourt court="Boutique" long="Boutique Bambu" />{" "}
            <span aria-hidden="true">{FLECHE_SORTIE}</span>
          </a>
        )}
      </div>
      <ul>
        {groupe.lignes.map((ligne) => (
          <li
            className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-2.5 py-2"
            key={ligne.cle}
          >
            <Pastille hex={ligne.couleur_hex} />
            <div className="min-w-0">
              <p className="text-[14.5px]">{ligne.couleur_nom}</p>
              <p className="text-[12.5px] text-muted-foreground">{pourquoiEnClair(ligne)}</p>
            </div>
            <p className="whitespace-nowrap text-sm font-semibold text-racheter">
              <LongOuCourt
                court={
                  <>
                    {ligne.a_racheter}
                    <span className="sr-only"> à racheter</span>
                  </>
                }
                long={aRacheterEnClair(ligne.a_racheter)}
              />
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
